import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DownwardIcon, NoopIcon, UpwardIcon } from '../../../../components/Icon';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import { fetchUserVirtualB2BHistory } from '../../../../redux/modules/user/user.actions';
import { userVirtualB2BHistorySelector } from '../../../../redux/modules/user/user.selectors';
import { GridCellExpand, Table } from '../../../../components/Table';
import { round2Format } from '../../../../utils/number';

interface UserVirtualB2BHistoryTableProps {
  userId: number;
}

export function UserVirtualB2BHistoryTable({ userId }: UserVirtualB2BHistoryTableProps) {
  const columns = useRef<GridColDef[]>([
    { field: 'id', headerName: 'Id', filterable: false },
    { field: 'userBankDetailsId', headerName: 'User Bank Details Id', type: 'number' },
    {
      field: 'prevAmount',
      headerName: 'Prev Amount',
      minWidth: 100,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'prevHold',
      headerName: 'Prev Hold',
      minWidth: 100,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'prevCashAllocationValue',
      headerName: 'Prev Cash Allocation Value',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'prevUnsettledFee',
      headerName: 'Prev Unsettled Fee',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'prevWithdrawableAmount',
      headerName: 'Prev Withdrawable Amount',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      minWidth: 100,
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        let Icon = NoopIcon;

        if (params.value > 0) {
          Icon = UpwardIcon;
        }

        if (params.value < 0) {
          Icon = DownwardIcon;
        }

        return (
          <>
            <Icon />
            {round2Format(params.value)}
          </>
        );
      },
    },
    {
      field: 'hold',
      headerName: 'Hold',
      minWidth: 100,
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        let Icon = NoopIcon;

        if (params.value > 0) {
          Icon = UpwardIcon;
        }

        if (params.value < 0) {
          Icon = DownwardIcon;
        }

        return (
          <>
            <Icon />
            {round2Format(params.value)}
          </>
        );
      },
    },
    {
      field: 'cashAllocationValue',
      headerName: 'Cash Allocation Value',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        let Icon = NoopIcon;

        if (params.value > 0) {
          Icon = UpwardIcon;
        }

        if (params.value < 0) {
          Icon = DownwardIcon;
        }

        return (
          <>
            <Icon />
            {round2Format(params.value)}
          </>
        );
      },
    },
    {
      field: 'unsettledFee',
      headerName: 'Unsettled Fee',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        let Icon = NoopIcon;

        if (params.value > 0) {
          Icon = UpwardIcon;
        }

        if (params.value < 0) {
          Icon = DownwardIcon;
        }

        return (
          <>
            <Icon />
            {round2Format(params.value)}
          </>
        );
      },
    },
    {
      field: 'withdrawableAmount',
      headerName: 'Withdrawable Amount',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => {
        let Icon = NoopIcon;

        if (params.value > 0) {
          Icon = UpwardIcon;
        }

        if (params.value < 0) {
          Icon = DownwardIcon;
        }

        return (
          <>
            <Icon />
            {round2Format(params.value)}
          </>
        );
      },
    },
    {
      field: 'newAmount',
      headerName: 'New Amount',
      minWidth: 100,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'newHold',
      headerName: 'New Hold',
      minWidth: 100,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'newCashAllocationValue',
      headerName: 'New Cash Allocation Value',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'newUnsettledFee',
      headerName: 'New Unsettled Fee',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'newWithdrawableAmount',
      headerName: 'New Withdrawable Amount',
      minWidth: 150,
      type: 'number',
      flex: 1,
      renderCell: (params) => round2Format(params.value),
    },
    {
      field: 'metadata',
      headerName: 'Metadata',
      sortable: false,
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <GridCellExpand value={JSON.stringify(params.value, null, 2) || ''} width={params.colDef.computedWidth} />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      type: 'dateTime',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) {
          return '';
        }

        return params.formattedValue;
      },
    },
  ]);
  const { userVirtualB2BHistory, totalUserVirtualB2BHistory, fetchUserVirtualB2BHistoryStatus } =
    useSelector(userVirtualB2BHistorySelector);

  return (
    <Table
      fetchItems={fetchUserVirtualB2BHistory}
      rows={userVirtualB2BHistory}
      fetchItemsCustomParams={{ userId }}
      columns={columns.current}
      loading={fetchUserVirtualB2BHistoryStatus === fetchStatuses.pending}
      rowCount={totalUserVirtualB2BHistory}
      disableRowGrouping
      density="compact"
      initialColumnVisibilityModel={{ id: false, userId: false, metadata: false }}
      initialSortModel={[{ field: 'id', sort: 'desc' }]}
      toolbarExportProps={{
        csvOptions: { fileName: `user-virtual-b2b-history-${userId}-${new Date().toLocaleString()}` },
        excelOptions: { fileName: `user-virtual-b2b-history-${userId}-${new Date().toLocaleString()}` },
      }}
      wrapperHeight={300}
    />
  );
}
